/**
 * Quien puede ASIGNAR una tarea, y a QUIEN (#534) — carril web.
 *
 * Espejo exacto de `mcp-server/src/utils/taskAssignmentPermissions.js`. Las dos
 * copias existen porque el frontend y el servidor MCP son paquetes distintos y
 * no comparten build (mismo patron que `materialSplit.js` y
 * `rawTextFidelity.js`). Si cambia una regla, cambian las DOS.
 *
 * EL AGUJERO QUE ESTO TAPA
 * ------------------------
 * Asignar una tarea no validaba ni la relacion del que asigna con el proyecto
 * ni si la persona asignada es personal de la empresa. Como
 * `has_project_stake()` cuenta cualquier fila de `task_assignments` como
 * pertenencia, autoasignarse una tarea ajena convertia al tecnico en miembro
 * del proyecto entero y los techos de #488/#523 lo dejaban pasar por la puerta.
 *
 * La ley es la RLS (`supabase/migrations/20260729110000_techo_asignaciones.sql`);
 * esto es lo que lo explica antes de que el insert llegue a la base, porque un
 * rebote 42501 no orienta a nadie.
 *
 * @module utils/taskAssignmentPermissions
 */

/** `employee` es el nombre viejo del mismo rol — ver `is_technician()`. */
const TECHNICIAN_ROLES = ['technician', 'employee'];

/** Personal interno: los unicos que pueden quedar asignados a una tarea. */
export const INTERNAL_STAFF_ROLES = ['admin', 'supervisor', 'technician', 'employee'];

/** Roles que pueden asignar en cualquier proyecto, sin participar de el. */
export const ASSIGNER_ROLES_WITHOUT_STAKE = ['admin', 'supervisor'];

export const isTechnicianRole = (role) => TECHNICIAN_ROLES.includes(role);

export const isInternalStaffRole = (role) => INTERNAL_STAFF_ROLES.includes(role);

/** Un rol desconocido o ausente NO entra: se falla cerrado. */
export const canAssignWithoutStake = (role) => ASSIGNER_ROLES_WITHOUT_STAKE.includes(role);

/**
 * @param {string} role             rol del que asigna
 * @param {boolean} callerHadStake  ¿participaba del proyecto ANTES de esta llamada?
 */
export const canAssignInProject = (role, callerHadStake) => {
  if (canAssignWithoutStake(role)) return true;
  if (!isInternalStaffRole(role)) return false;
  return callerHadStake === true;
};

export const ASSIGNER_SCOPE_DENIAL_MESSAGE =
  'No se puede asignar esa tarea: solo puede repartir el trabajo de un proyecto quien ya participa '
  + 'de el (tiene una tarea asignada ahi, lidera su cotizacion o lo creo), o un supervisor/admin. '
  + 'Pedile a un supervisor que haga la asignacion.';

export const assigneeRoleDenialMessage = (assigneeRole) => {
  if (assigneeRole === 'client') {
    return 'No se puede asignar una tarea interna a un cliente: las tareas las ejecuta el personal '
      + 'de ProMedic. Si el cliente tiene que enterarse, compartile el avance del proyecto.';
  }
  return `No se puede asignar la tarea a esa persona (rol "${assigneeRole || 'desconocido'}"): `
    + `solo se asignan tareas a personal interno (${INTERNAL_STAFF_ROLES.join(', ')}).`;
};

export const ASSIGNEE_NOT_FOUND_MESSAGE =
  'No se puede asignar la tarea: el usuario indicado no existe.';

export const ASSIGNMENT_SCOPE_RLS_MESSAGE =
  'La base rechazo la asignacion: solo se asignan tareas de proyectos donde el usuario participa, '
  + 'y solo a personal interno.';

// ---------------------------------------------------------------------
// CREAR una tarea (#536) — la otra mitad de #534
//
// `technician_create_tasks` es un chequeo de rol sin ambito, asi que un tecnico
// podia crear una tarea en cualquiera de los 73 proyectos. La ley es el techo
// `tasks_ceiling_insert` de 20260730000000_techo_creacion_tareas.sql; esto lo
// explica antes del rebote 42501.
// ---------------------------------------------------------------------

/** Roles que crean tareas en cualquier proyecto, sin participar de el. */
export const TASK_CREATOR_ROLES_WITHOUT_STAKE = ASSIGNER_ROLES_WITHOUT_STAKE;

export const canCreateTaskWithoutStake = (role) =>
  TASK_CREATOR_ROLES_WITHOUT_STAKE.includes(role);

/**
 * @param {string} role             rol del que crea
 * @param {boolean} callerHasStake  ¿participa del proyecto?
 */
export const canCreateTaskInProject = (role, callerHasStake) => {
  if (canCreateTaskWithoutStake(role)) return true;
  if (!isInternalStaffRole(role)) return false;
  return callerHasStake === true;
};

export const TASK_CREATE_SCOPE_DENIAL_MESSAGE =
  'No se puede crear una tarea en ese proyecto: solo puede hacerlo quien ya participa de el '
  + '(tiene una tarea asignada ahi, lidera su cotizacion o lo creo), o un supervisor/admin.';

const taskAssignmentPermissions = {
  INTERNAL_STAFF_ROLES,
  ASSIGNER_ROLES_WITHOUT_STAKE,
  TASK_CREATOR_ROLES_WITHOUT_STAKE,
  isTechnicianRole,
  isInternalStaffRole,
  canAssignWithoutStake,
  canAssignInProject,
  canCreateTaskWithoutStake,
  canCreateTaskInProject,
  ASSIGNER_SCOPE_DENIAL_MESSAGE,
  assigneeRoleDenialMessage,
  ASSIGNEE_NOT_FOUND_MESSAGE,
  ASSIGNMENT_SCOPE_RLS_MESSAGE,
  TASK_CREATE_SCOPE_DENIAL_MESSAGE
};

export default taskAssignmentPermissions;
